import React, { useState } from 'react';
import { Panel, StackedBars, LineChart, Legend, Empty, StatusIcon, KpiRow, usePersisted } from './charts';

const LEVEL = {
  important: { label: 'Важно', cls: 'an-st-critical', icon: 'critical' },
  warning: { label: 'Внимание', cls: 'an-st-warning', icon: 'warning' },
  note: { label: 'Заметка', cls: 'an-st-note', icon: 'warning' },
};
const ALERTS_COLLAPSED = 5;

/** «Требует внимания» — сразу под показателями: владелец ищет это первым */
function Alerts({ items, onOpenTab }) {
  const [all, setAll] = useState(false);
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, ALERTS_COLLAPSED);
  return (
    <Panel title="Требует внимания">
      {shown.map((a, i) => {
        const lv = LEVEL[a.level] || LEVEL.note;
        return (
          <div key={i} className="an-alert">
            <span className={`an-alert-tag ${lv.cls}`} style={{ '--ch': '5px' }}>
              <StatusIcon status={lv.icon} />{lv.label}
            </span>
            <div className="an-alert-body">
              <span>{a.text}</span>
              {a.tab && <button type="button" className="an-link" onClick={() => onOpenTab(a.tab)}>{a.link || 'Открыть'}</button>}
            </div>
          </div>
        );
      })}
      {items.length > ALERTS_COLLAPSED && (
        <button type="button" className="an-link" onClick={() => setAll(v => !v)}>
          {all ? 'Свернуть' : `Ещё ${items.length - ALERTS_COLLAPSED}`}
        </button>
      )}
    </Panel>
  );
}

const BarsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="12" width="4" height="9" rx="1" /><rect x="10" y="6" width="4" height="15" rx="1" /><rect x="17" y="9" width="4" height="12" rx="1" />
  </svg>
);
const LineIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 17c3-5 5-8 8-6s4 3 6-1 3-5 4-5" />
  </svg>
);

/** Вид графика — значками (владелец 13.10): столбики по каналам или линия */
function ChartKind({ value, onChange }) {
  return (
    <span className="seg an-chart-kind" role="group" aria-label="Вид графика">
      <button type="button" className={`seg-btn${value === 'bars' ? ' on' : ''}`} onClick={() => onChange('bars')} aria-pressed={value === 'bars'}
        title="Столбики по каналам" aria-label="Столбики по каналам"><BarsIcon /></button>
      <button type="button" className={`seg-btn${value === 'line' ? ' on' : ''}`} onClick={() => onChange('line')} aria-pressed={value === 'line'}
        title="Линия" aria-label="Линия"><LineIcon /></button>
    </span>
  );
}

/** «Сводка»: прибыль, оборот, заказы, средний чек; что требует внимания; оборот по дням (13.10) */
export default function OverviewTab({ data, blocks, compare, onOpenTab, onExpenses }) {
  const has = (b) => blocks.includes(b);
  const chart = data.chart;
  const [kind, setKind] = usePersisted('nf_an_chart', 'bars');
  const compareLabel = data.period?.compare === 'year' ? 'год назад' : 'прошлый период';
  return (
    <div className="an-stack">
      {/* Какие плитки первыми — решает сервер (data.hero): с затратами впереди операционная прибыль */}
      {has('tiles') && <KpiRow tiles={data.tiles} keys={data.hero || ['sales_profit', 'turnover', 'orders', 'avg_check']} compare={compare} />}
      {/* Затрат нет — подсказка, где их внести (аудит 14.10: владелец не нашёл переключатель в «Финансах») */}
      {data.costs && !data.costs.any && onExpenses && (
        <div className="an-muted an-small">
          Внесите аренду и зарплату — увидите, сколько зарабатываете после всех затрат.{' '}
          <button type="button" className="an-link" onClick={onExpenses}>Внести затраты →</button>
        </div>
      )}

      {has('alerts') && <Alerts items={data.alerts} onOpenTab={onOpenTab} />}

      {has('turnover_by_channel') && (
        <Panel title={`Оборот ${chart.weekly ? 'по неделям' : 'по дням'}`}
          aside={<ChartKind value={kind} onChange={setKind} />}>
          {chart.buckets.some(b => b.total > 0) ? (kind === 'line' ? (
            <>
              <div className="an-muted an-small" style={{ marginBottom: 8 }}>
                Сплошная — этот период, пунктир — {compareLabel}. {data.period?.partial ? 'Последняя точка — день ещё идёт. ' : ''}Нажмите на день — справа цифры
              </div>
              <LineChart buckets={chart.buckets} compareLabel={compareLabel} showPrev={compare} />
            </>
          ) : (
            <>
              <Legend items={chart.channels.map(c => ({ ...c, extra: `${c.share} %` }))} />
              <StackedBars buckets={chart.buckets} channels={chart.channels} />
              {data.period?.partial && <div className="an-muted an-small" style={{ marginTop: 6 }}>Светлый столбик — сегодня, день ещё идёт</div>}
            </>
          )) : <Empty />}
        </Panel>
      )}

      {has('how_we_count') && (
        <details className="an-how">
          <summary>Как считаем</summary>
          <div className="an-text">
            <p><b>Оборот</b> — что заплатили покупатели за заказы без отменённых: у своих каналов вместе с оплатой бонусами и сертификатами, у площадок — по их ценам. День заказа — по дате, выбранной вверху.</p>
            <p><b>Осталось магазину</b> — оборот минус комиссии площадок, эквайринг и оплата бонусами и сертификатами. Пока площадка не прислала свои цифры по заказу, удержание считается по фактической ставке недавних заказов и помечено как оценка.</p>
            <p><b>Валовая прибыль</b> — осталось магазину минус закупочная стоимость цветов и упаковки и выплаты курьерам. <b>Маржинальная прибыль</b> — ещё минус списания: всё, что растёт вместе с продажами. Аренда, зарплаты, реклама и налоги в неё не входят. Брак при приёмке не считаем: он уходит поставщику по рекламации.</p>
            <p><b>Операционная прибыль</b> — маржинальная прибыль минус продвижение и постоянные затраты из «Финансы → Затраты»: аренда, зарплата, коммуналка, сервисы, налоги, оборудование — доля по дням выбранного периода. Пока месяц там не подтверждён, затраты считаются по плану. Появляется, когда затраты внесены; с ней — точка безубыточности: оборот, при котором прибыль равна нулю.</p>
            <p className="an-muted">Те же цифры — в утреннем сообщении и в «Финансах»: показатели считаются в одном месте.</p>
          </div>
        </details>
      )}
    </div>
  );
}
